import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Snowflake, Clock, CalendarDays, Sun, Feather } from "lucide-react";
import { Reveal } from "@/components/pdp/reveal";
import { useProduct } from "@/components/pdp/PdpDataContext";

const ICONS = { snowflake: Snowflake, clock: Clock, calendar: CalendarDays, sun: Sun, feather: Feather };

export const StoryReset = () => {
  const { storyA } = useProduct();
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], reduce ? ["0%", "0%"] : ["-8%", "8%"]);

  return (
    <section ref={ref} className="bg-white py-20 lg:py-32 overflow-hidden" data-testid="story-reset">
      <div className="max-w-[1600px] mx-auto px-4 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-6 relative">
          <div className="editorial-frame aspect-[4/5] bg-riversol-sand group">
            <motion.img
              style={{ y }}
              src={storyA.image}
              alt={storyA.imageAlt}
              className="w-full h-[116%] object-cover -mt-[8%]"
            />
          </div>
          <div className="absolute -bottom-8 -right-6 w-44 h-52 editorial-frame border-4 border-white bg-riversol-stone shadow-[0_20px_50px_rgba(0,0,0,0.08)] hidden xl:block">
            <img src={storyA.detailImage} alt={storyA.detailAlt} className="w-full h-full object-cover" />
          </div>
        </div>

        <div className="lg:col-span-6 lg:pl-4">
          <div className="pdp-eyebrow font-mono text-[11px] tracking-[0.24em] uppercase text-riversol-blue mb-6">
            {storyA.eyebrow}
          </div>
          <h2 className="font-mono text-3xl md:text-4xl lg:text-5xl text-neutral-900 mb-8 tracking-tight leading-[1.05]">
            {storyA.title}
          </h2>
          <Reveal>
            <p className="text-[16px] lg:text-[17px] leading-[1.75] lg:leading-[1.8] text-neutral-600 max-w-xl mb-8 lg:mb-10">
              {storyA.body}
            </p>
          </Reveal>
          <div className="flex flex-wrap items-center gap-x-8 gap-y-4 lg:gap-10 pt-6 lg:pt-8 border-t border-neutral-200">
            {storyA.indicators.map(({ icon, label }) => {
              const Icon = ICONS[icon] || Snowflake;
              return (
                <div key={label} className="flex items-center gap-3">
                  <Icon size={20} className="text-riversol-blue" strokeWidth={1.5} />
                  <span className="font-mono text-[11px] tracking-[0.12em] uppercase text-neutral-600">{label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default StoryReset;
